if (!customElements.get('mobile-peek-gallery')) {
  customElements.define(
    'mobile-peek-gallery',
    class MobilePeekGallery extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('.mobile-peek-gallery__track');
        this.slides = Array.from(this.querySelectorAll('.mobile-peek-gallery__slide'));
        this.dots = Array.from(this.querySelectorAll('.mobile-peek-gallery__dot'));
        this.counter = this.querySelector('.mobile-peek-gallery__counter');
        this.counterCurrent = this.counter?.querySelector('.mobile-peek-gallery__counter-current');
        this.counterTotal = this.counter?.querySelector('.mobile-peek-gallery__counter-total');
        this.isExpanded = false;
        this.currentIndex = 0;
        this.filteringOption = this.dataset.filteringOption;
        this.productSpecificFiltering = this.dataset.productSpecificFiltering === 'true';
        this.sectionId = this.dataset.section;

        this.init();
      }

      init() {
        if (!this.track || this.slides.length === 0) return;

        // Each slide has its own expand button
        this.slides.forEach((slide) => {
          const btn = slide.querySelector('.mobile-peek-gallery__expand-btn');
          if (btn) {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
              this.toggleExpand();
            });
          }
        });

        // Track scroll for active slide detection
        this.track.addEventListener('scroll', this.debounce(() => this.onScroll(), 60), { passive: true });

        // Dot clicks
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this.goToSlide(i));
        });

        // Variant change filtering
        if (this.filteringOption) {
          document.addEventListener('variant:change', (e) => this.handleVariantChange(e));
        }

        this.updateCounter();
      }

      toggleExpand() {
        this.isExpanded = !this.isExpanded;
        this.classList.toggle('is-expanded', this.isExpanded);

        const visibleSlides = this.getVisibleSlides();
        const currentSlide = visibleSlides[this.currentIndex];
        if (!currentSlide) return;

        requestAnimationFrame(() => {
          setTimeout(() => {
            if (this.isExpanded) {
              currentSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
            } else {
              this.track.scrollTo({
                left: currentSlide.offsetLeft,
                behavior: 'smooth',
              });
            }
          }, 50);
        });
      }

      onScroll() {
        const visibleSlides = this.getVisibleSlides();
        if (visibleSlides.length === 0) return;

        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width * 0.4;

        let closestIndex = 0;
        let closestDistance = Infinity;

        visibleSlides.forEach((slide, i) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - trackCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = i;
          }
        });

        if (closestIndex !== this.currentIndex) {
          this.currentIndex = closestIndex;
          this.updateActiveDot();
          this.updateCounter();
        }
      }

      goToSlide(index) {
        const visibleSlides = this.getVisibleSlides();
        const slide = visibleSlides[index];
        if (!slide) return;

        if (this.isExpanded) {
          slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        } else {
          this.track.scrollTo({
            left: slide.offsetLeft,
            behavior: 'smooth',
          });
        }
        this.currentIndex = index;
        this.updateActiveDot();
        this.updateCounter();
      }

      updateActiveDot() {
        const visibleDots = this.dots.filter((dot, i) => {
          const slide = this.slides[i];
          const isHidden = slide && slide.classList.contains('hidden');
          dot.style.display = isHidden ? 'none' : '';
          return !isHidden;
        });

        visibleDots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.currentIndex);
        });
      }

      updateCounter() {
        const visibleSlides = this.getVisibleSlides();
        if (this.counterCurrent) this.counterCurrent.textContent = this.currentIndex + 1;
        if (this.counterTotal) this.counterTotal.textContent = visibleSlides.length;
      }

      getVisibleSlides() {
        return this.slides.filter((s) => !s.classList.contains('hidden'));
      }

      handleVariantChange(event) {
        if (!this.filteringOption || !event.detail.variant) return;
        const currentVariant = event.detail.variant;

        this.slides.forEach((slide) => {
          const slideAlt = slide.dataset.alt;
          const shouldShow =
            slideAlt === currentVariant[this.filteringOption] ||
            slideAlt === 'always_display' ||
            !slideAlt ||
            slideAlt === '';
          slide.classList.toggle('hidden', !shouldShow);
        });

        this.currentIndex = 0;
        const firstVisible = this.getVisibleSlides()[0];
        if (firstVisible) {
          this.track.scrollTo({ left: 0, behavior: 'auto' });
        }
        this.updateActiveDot();
        this.updateCounter();
      }

      debounce(fn, delay) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), delay);
        };
      }
    }
  );
}
